import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, TrendingUp, Users, CloudSun, CalendarCheck, BrainCircuit, ShieldCheck, ChevronDown, Check, AlertTriangle, Minus, ArrowRight, Clock } from "lucide-react";

export default function DayModal({ propId, day, open, onClose, onLogged }) {
  const { t, lang } = useApp();
  const [detail, setDetail] = useState(day);
  const [loading, setLoading] = useState(false);
  const [yourPrice, setYourPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const [showConf, setShowConf] = useState(false);

  useEffect(() => {
    if (!open || !day) return;
    setDetail(day); setLoading(true); setYourPrice(String(day.recommended_price ?? ""));
    api.get(`/properties/${propId}/day/${day.date}?lang=${lang}`)
      .then(({ data }) => { setDetail(data); setYourPrice((p) => p || String(data.recommended_price ?? "")); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [open, day, propId, lang]);

  const saveDecision = async () => {
    const set_price = Number(yourPrice);
    if (!set_price || set_price <= 0) return;
    setSaving(true);
    try {
      await api.post("/price-changes", {
        property_id: propId, date: detail.date,
        suggested_price: detail.recommended_price, set_price, demand_index: detail.demand_index,
      });
      toast.success(t("priceLogged"));
      onClose(false);
      onLogged && onLogged();
    } catch {
      toast.error("Error");
    } finally { setSaving(false); }
  };

  if (!detail) return null;
  const color = detail.color === "red" ? "var(--demand-high)" : detail.color === "yellow" ? "var(--demand-medium)" : "var(--demand-low)";
  const dateStr = new Date(detail.date).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="day-modal" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="capitalize text-2xl">{dateStr}</DialogTitle>
          <DialogDescription className="sr-only">{t("why")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <span className="px-2 py-1 rounded text-xs font-bold text-white" style={{ backgroundColor: color }}>
            {t("demand")}: {detail.demand_index}/100
          </span>
          <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {t("competitionIndex")}: {detail.competition_index}</span>
          {detail.confidence != null && (
            <button data-testid="day-confidence" onClick={() => setShowConf((s) => !s)}
              className="text-xs font-bold px-2 py-1 rounded flex items-center gap-1 hover:ring-2 hover:ring-primary/30 transition"
              style={{ backgroundColor: `color-mix(in srgb, ${detail.confidence >= 0.7 ? "var(--demand-low)" : detail.confidence >= 0.5 ? "var(--demand-medium)" : "var(--demand-high)"} 20%, transparent)` }}>
              {t("confidence")}: {Math.round(detail.confidence * 100)}% <ChevronDown className={`w-3 h-3 transition-transform ${showConf ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>

        {showConf && (
          <div data-testid="confidence-detail" className="mt-3 bg-secondary/60 border border-border rounded-lg p-4">
            {!detail.confidence_detail ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2"><ShieldCheck className="w-4 h-4 animate-pulse" /> {t("loading")}</p>
            ) : (
            <>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">{t("whyConfidence")}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {detail.confidence_detail.level === "high" ? t("confHigh") : detail.confidence_detail.level === "medium" ? t("confMedium") : t("confLow")}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{t("dataUsed")}</p>
            <div className="space-y-1.5">
              {detail.confidence_detail.factors.map((f) => (
                <div key={f.key} className="flex items-start gap-2 text-sm">
                  {f.status === "ok" ? <Check className="w-4 h-4 text-[color:var(--demand-low)] mt-0.5 shrink-0" />
                    : f.status === "warning" ? <AlertTriangle className="w-4 h-4 text-[color:var(--demand-medium)] mt-0.5 shrink-0" />
                    : <Minus className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />}
                  <span className="flex-1">
                    <span className="font-medium">{lang === "it" ? f.label_it : f.label_en}</span>
                    {f.impact_pct > 0 && <span className="ml-2 text-[11px] font-data text-[color:var(--demand-low)]">+{f.impact_pct}%</span>}
                    <span className="block text-xs text-muted-foreground">{lang === "it" ? f.explanation_it : f.explanation_en}</span>
                  </span>
                </div>
              ))}
            </div>
            {detail.confidence_detail.advice.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-[10px] uppercase tracking-wider text-[color:var(--demand-medium)] font-bold mb-2">{t("howToImprove")}</p>
                <ul className="space-y-1.5">
                  {detail.confidence_detail.advice.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <ArrowRight className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />{lang === "it" ? a.it : a.en}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {detail.price_read_check?.mismatch && (
          <div className="mt-3 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs" data-testid="day-read-mismatch">
            <span className="font-semibold text-amber-500">⚠️ </span>
            {lang === "it"
              ? `Prezzo letto ora su Booking €${detail.price_read_check.read}, diverso dal prezzo ${detail.price_read_check.applied_source === "manual" ? "impostato" : "applicato"} €${detail.price_read_check.applied} (scarto ${detail.price_read_check.diff_pct}%).`
              : `Price now read on Booking €${detail.price_read_check.read}, different from your ${detail.price_read_check.applied_source === "manual" ? "set" : "applied"} price €${detail.price_read_check.applied} (${detail.price_read_check.diff_pct}% gap).`}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 my-4">
          {[["recommended", detail.recommended_price], ["minPrice", detail.min_price], ["maxPrice", detail.max_price]].map(([k, v]) => (
            <div key={k} className="bg-secondary rounded-md p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t(k)}</p>
              <p className="font-data text-xl font-semibold">€{v}</p>
            </div>
          ))}
        </div>
        {detail.current_price_asof && (
          <div className="-mt-2 mb-3 flex items-center justify-center gap-1 text-[11px] text-muted-foreground" data-testid="day-price-asof">
            <Clock className="w-3 h-3" />
            {lang === "it" ? "Prezzo aggiornato il" : "Price updated on"} {new Date(detail.current_price_asof).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}
          </div>
        )}
          <div className="grid grid-cols-2 gap-3 -mt-1 mb-4" data-testid="day-price-breakdown">
            <div className="bg-secondary/50 border border-border/60 rounded-md p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{lang === "it" ? "Con tassa di soggiorno" : "With city tax"}</p>
              <p className="font-data text-lg font-semibold">€{detail.recommended_price_gross ?? detail.recommended_price}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{lang === "it" ? `+€${detail.city_tax_night || 0}/notte tassa` : `+€${detail.city_tax_night || 0}/night tax`}</p>
            </div>
            <div className="bg-secondary/50 border border-border/60 rounded-md p-3 text-center" data-testid="day-net-price">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{lang === "it" ? "Netto (dopo i tuoi sconti)" : "Net (after your discounts)"}</p>
              <p className="font-data text-lg font-semibold">{detail.recommended_price_net != null ? `€${detail.recommended_price_net}` : "€" + detail.recommended_price}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{detail.self_offer_pct ? (lang === "it" ? `sconto ${detail.self_offer_pct}%` : `discount ${detail.self_offer_pct}%`) : (lang === "it" ? "nessuno sconto impostato" : "no discount set")}</p>
            </div>
          </div>
        )}
        {detail.premium_floor?.applied && (
          <div className="-mt-1 mb-4 rounded-md border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-[11px]" data-testid="day-premium-floor">
            <span className="font-semibold text-emerald-600">{lang === "it" ? "Camera premium" : "Premium room"}: </span>
            {(() => {
              const mm = detail.premium_floor.period_month;
              const names = lang === "it"
                ? ["", "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"]
                : ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
              const per = mm ? (lang === "it" ? ` di ${names[parseInt(mm, 10)]}` : ` for ${names[parseInt(mm, 10)]}`) : "";
              return lang === "it"
                ? `non scendo sotto il minimo storico${per} di €${detail.premium_floor.floor_net} (netto di tasse e sconti).`
                : `not going below the €${detail.premium_floor.floor_net} historical minimum${per} (net of tax & discounts).`;
            })()}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("occupancy")}</p><p className="font-data text-lg font-semibold">{detail.occupancy_pct}%</p></div>
          <div className="text-center"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("revenue")}</p><p className="font-data text-lg font-semibold">€{detail.projected_revenue}</p></div>
          <div className="text-center"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("sellProb")}</p><p className="font-data text-lg font-semibold">{Math.round(detail.sell_probability * 100)}%</p></div>
        </div>

        <div className="bg-accent/60 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">{t("why")}</span>
          </div>
          {detail.ai?.summary && <p className="text-sm mb-3">{detail.ai.summary}</p>}
          <ul className="space-y-2">
            {((detail.ai?.reasons && detail.ai.reasons.length ? detail.ai.reasons : null) || detail.factors?.map((f) => f[lang === "it" ? "detail_it" : "detail_en"]) || []).map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <TrendingUp className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />{r}
              </li>
            ))}
          </ul>
          {loading && !detail.ai && (
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 animate-pulse" /> {lang === "it" ? "L'AI sta elaborando la spiegazione..." : "AI is refining the explanation..."}
            </p>
          )}
        </div>

        {detail.weather && (
          <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
            <CloudSun className="w-4 h-4" /> {Math.round(detail.weather.t_min)}° / {Math.round(detail.weather.t_max)}°C
          </div>
        )}
        {detail.holiday && (
          <div className="flex items-center gap-2 mt-1 text-sm text-primary font-medium">
            <CalendarCheck className="w-4 h-4" /> {detail.holiday}
          </div>
        )}
        {detail.events && detail.events.length > 0 && (
          <div className="mt-3" data-testid="day-events">
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("events")}</p>
              {detail.event_impact > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: "color-mix(in srgb, var(--demand-high) 18%, transparent)", color: "var(--demand-high)" }}>
                  {t("influencedPrice")} · +{detail.event_impact}
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              {detail.events.map((e, i) => {
                const cat = lang === "it" ? (e.category_it || e.category) : (e.category_en || e.category);
                const lvlColor = e.impact_level === "high" ? "var(--demand-high)" : e.impact_level === "medium" ? "var(--demand-medium)" : "var(--demand-low)";
                return (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs bg-accent/60 rounded px-2 py-1.5">
                    <span className="flex-1 truncate">
                      <span className="font-medium">{e.name}</span>
                      {cat ? <span className="text-muted-foreground"> · {cat}</span> : ""}
                      {e.distance_km != null ? <span className="text-muted-foreground"> · {e.distance_km} km</span> : ""}
                    </span>
                    {e.impact != null && (
                      <span className="shrink-0 font-data font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `color-mix(in srgb, ${lvlColor} 18%, transparent)`, color: lvlColor }}>+{e.impact}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-5 border-t border-border pt-4">
          <div className="flex items-center gap-2 mb-2">
            <BrainCircuit className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">{t("setYourPrice")}</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">{t("learningHint")}</p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("yourPrice")} (€)</label>
              <Input data-testid="your-price-input" type="number" value={yourPrice} onChange={(e) => setYourPrice(e.target.value)} className="mt-1" />
            </div>
            <Button data-testid="save-price-btn" onClick={saveDecision} disabled={saving}>{saving ? "…" : t("savePrice")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
